import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnChanges,
  SimpleChanges,
  TrackByFunction,
  input,
} from '@angular/core';

import {Router} from '@angular/router';
import {ProjectChapterType, ProjectDetailResponse} from '@frontend/shared-api';
import {ProjectChapterProgressCardComponent} from '../components/chapter-progress-card/project-chapter-progress-card.component';
import {ProjectStepStateService} from '../../../project-sequence/project-step-state.service';
import {Chapter} from '../../../project-sequence/project-step.types';
import {CardListComponent} from '../../../../../shared/components/card/card-list/card-list.component';
import {ProjectDataService} from '../../../../../shared/services/project-data.service';
import {ProjectSideMenuAccessControlService} from '../../../project-sequence/project-side-menu/project-side-menu-access-control.service';
import {ProjectUserDetailService} from '../../../../../shared/services/project-user-detail.service';
import {APP_ROUTES} from '../../../../../app.routes';

@Component({
  selector: 'app-project-detail-list',
  imports: [ProjectChapterProgressCardComponent, CardListComponent],
  templateUrl: './project-detail-list.component.html',
})
export class ProjectDetailListComponent implements OnChanges {
  readonly project = input.required<ProjectDetailResponse>();

  private readonly stepStateService = inject(ProjectStepStateService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly router = inject(Router);
  private readonly projectUserDetailService = inject(ProjectUserDetailService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accessControlService = inject(ProjectSideMenuAccessControlService);
  readonly projectId = computed(() => this.project()?.id);
  private readonly userDetail = this.projectUserDetailService.createUserDetailSignal(
    this.projectId,
    this.destroyRef,
  );

  get chapters(): Chapter[] {
    return this.stepStateService.chaptersWithStatuses();
  }

  trackByChapterId: TrackByFunction<Chapter> = (_, chap) => chap.configId;

  ngOnChanges(changes: SimpleChanges): void {
    const project = this.project();
    if (changes['project'] && project) {
      this.projectDataService.setProject(project);
    }
  }

  isChapterAccessible(chapter: Chapter): boolean {
    return this.accessControlService.isChapterAccessible(chapter, this.userDetail());
  }

  goToChapter(chapterId: ProjectChapterType) {
    const chapters = this.chapters;
    const chapter = chapters.find((ch) => ch.configId === chapterId);

    if (!chapter) {
      console.warn(`Chapter ${chapterId} not found!`);
      return;
    }
    if (!this.isChapterAccessible(chapter)) {
      console.warn(`User does not have access to chapter ${chapterId}`);
      return;
    }
    const firstAccessibleStep = this.accessControlService.findFirstAccessibleStep(
      [chapter],
      this.userDetail(),
    );
    if (!firstAccessibleStep) {
      return;
    }

    this.stepStateService.clearActiveSpecialOption();
    this.stepStateService.setActiveStep(chapterId, firstAccessibleStep.stepId);

    this.router.navigate([APP_ROUTES.PROJECT, this.project().id, APP_ROUTES.STEPS]);
  }
}
